import { useMemo } from 'react'
import {
  ICellRendererParams,
  IsFullWidthRowParams,
} from '@ag-grid-community/core'
import { DataTable } from 'components/common'
import { CronJob, CronJobRow } from 'types/cronJob'
import { getCronJobColumns } from './columns'

type Props = {
  jobs: CronJob[]
  loading: boolean
  isEditor: boolean
  onEdit: (job: CronJob) => void
  onReset: (job: CronJob) => void
}

const ROW_HEIGHT = 60
const GROUP_ROW_HEIGHT = 30

// Jobs sorted by group, with one header row in front of each group
const buildGroupedRows = (jobs: CronJob[]): CronJobRow[] => {
  const byGroup: Record<string, CronJob[]> = {}

  jobs.forEach((job) => {
    const group = job.group ?? 'Other'
    byGroup[group] = [...(byGroup[group] ?? []), job]
  })

  return Object.keys(byGroup)
    .sort((a, b) => a.localeCompare(b))
    .flatMap((group): CronJobRow[] => [
      {
        _id: `group:${group}`,
        isGroupRow: true,
        group,
        count: byGroup[group].length,
      },
      ...byGroup[group],
    ])
}

// Full-width header: group name and how many jobs it has
const GroupRow = ({ data }: ICellRendererParams<CronJobRow>) => {
  if (!data?.isGroupRow) return null

  return (
    <div className="flex h-full items-center gap-2 bg-gray-50 px-3 text-xs font-semibold text-gray-600">
      {data.group}
      <span className="font-normal text-gray-400">{data.count} jobs</span>
    </div>
  )
}

// Compact grid grouped by job group; null rows show the loading overlay
const CronJobsTable = ({ jobs, loading, isEditor, onEdit, onReset }: Props) => {
  const columns = useMemo(
    () => getCronJobColumns(isEditor, onEdit, onReset),
    [isEditor],
  )

  const rows = useMemo(() => buildGroupedRows(jobs), [jobs])

  return (
    <DataTable
      columns={columns}
      rows={loading ? (null as unknown as CronJobRow[]) : rows}
      identifier="_id"
      isFullWidthRow={({ rowNode }: IsFullWidthRowParams) =>
        Boolean(rowNode.data?.isGroupRow)
      }
      fullWidthCellRenderer={GroupRow}
      getRowHeight={({ data }) =>
        data?.isGroupRow ? GROUP_ROW_HEIGHT : ROW_HEIGHT
      }
    />
  )
}

export default CronJobsTable
